import type { TFunction } from 'i18next'

export const SUPPORTED_LANG_CODES = ['en', 'fr', 'pt'] as const
export type SupportedLangCode = (typeof SUPPORTED_LANG_CODES)[number]

/** Normalize i18next language (e.g. en-US, pt-BR) to en | fr | pt. */
export function normalizeLangCode(language?: string): SupportedLangCode {
  const base = (language || 'fr').split('-')[0].toLowerCase()
  if (base === 'en' || base === 'pt') return base
  return 'fr'
}

export function moneyFormatLocale(lang: SupportedLangCode): string {
  if (lang === 'en') return 'en-CA'
  if (lang === 'pt') return 'pt-CA'
  return 'fr-CA'
}

export function dateFormatLocale(lang: SupportedLangCode): string {
  if (lang === 'en') return 'en-CA'
  if (lang === 'pt') return 'pt-CA'
  return 'fr-CA'
}

export function roomCountLabelKey(room: string): string | null {
  if (room === 'studio') return 'studio'
  if (room === '5.5') return '5_5'
  if (room === '5.5+') return '5_5p'
  if (room === '6.5+') return '6_5p'
  if (room === '2.5' || room === '3.5' || room === '4.5') {
    return room.replace('.', '_')
  }
  return null
}

export function tRoomCountLabel(t: TFunction, room: string): string {
  const key = roomCountLabelKey(room)
  if (!key) return room
  return t(`createAd.flow.rooms.${key}`)
}

export function tUnitTypeLabel(t: TFunction, unitType: string): string {
  return t(`createAd.flow.unitTypes.${unitType}`, { defaultValue: unitType })
}

export function tListingTypeLabel(t: TFunction, listingType: string): string {
  return t(`createAd.flow.listingTypes.${listingType}`, { defaultValue: listingType })
}

export function tFloorLabel(t: TFunction, floor: string, floorCustom?: string): string {
  if (!floor) return '—'
  const plusMatch = floor.match(/^5_plus:(\d+)$/)
  if (plusMatch) {
    return `${t('createAd.flow.floorOptions.5_plus')} (${plusMatch[1]})`
  }
  if (floor === '5_plus' && floorCustom?.trim()) {
    return `${t('createAd.flow.floorOptions.5_plus')} (${floorCustom.trim()})`
  }
  return t(`createAd.flow.floorOptions.${floor}`, { defaultValue: floor })
}

export function formatLocalizedDate(
  value: string,
  lang: SupportedLangCode,
  options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }
): string {
  if (!value.trim()) return ''
  // Parse YYYY-MM-DD as local calendar date to avoid UTC day-shift.
  const isoDay = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  const date = isoDay
    ? new Date(Number(isoDay[1]), Number(isoDay[2]) - 1, Number(isoDay[3]))
    : new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(dateFormatLocale(lang), options)
}
